import { AuthStatus } from '@/enums';
import { consumeBootStorefrontIntent } from '@domain/microproducts/boot-params';
import { ExperienceMode, useExperienceStore } from '@/stores/experience-store';
import {
  isAuthenticatedUserContext,
  useMaybeUserContext,
} from '@/contexts/user-context';
import { StorefrontBootIntent } from '@domain/microproducts/boot-params-types';
import { useCallback, useEffect, useState } from 'react';

interface StorefrontBootIntentLatch {
  clearPendingStorefrontNavigation: () => void;
  pendingStorefrontNavigation: StorefrontBootIntent | null;
}

/**
 * One-shot application of a /storefront/:productId boot URL.
 *
 * Must be called from app-stack, ABOVE the selectedRestaurantUUID key
 * boundary — the same level as the lastProcessedNotificationID latch and
 * for the same reason: state inside the keyed subtree dies on every
 * restaurant switch, and a seed effect inside it would re-apply on every
 * remount and revert manual switches. The intent itself is additionally
 * one-shot at the module level (consumeBootStorefrontIntent), so even a
 * full app-stack remount cannot re-apply it.
 *
 * The navigation is NOT performed here: the restaurant switch remounts the
 * keyed subtree, destroying nested stack state, so navigating before the
 * switch lands would be lost (only tab-level routes survive remounts, via
 * lastActiveTabRouteRef — the microproduct screen is stack-registered).
 * Instead the intent is persisted as pending state up here (the
 * use-cart-redirect persist-then-select pattern) and consumed by
 * useStorefrontBootNavigation inside the remounted subtree.
 */
export const useStorefrontBootIntent = (): StorefrontBootIntentLatch => {
  const context = useMaybeUserContext();
  const [pendingStorefrontNavigation, setPendingStorefrontNavigation] =
    useState<StorefrontBootIntent | null>(null);

  useEffect(() => {
    // Apply only for a fully authenticated context (guest/offline/
    // not-authorized contexts have no usable selectRestaurant) — which also
    // means the user query has resolved and the selected-restaurant
    // auto-correction has already run, the same readiness the notification
    // listener relies on. Until then the intent stays latched, un-consumed.
    if (!isAuthenticatedUserContext(context)) {
      // Terminal non-authenticated contexts (guest mode, authenticated but
      // not authorized) will never consume the intent — discard it so the
      // pending flag doesn't force the operator experience for the whole
      // session. The pre-login state (NotAuthenticated/null) keeps the
      // intent latched so the deep link survives sign-in.
      // Guest and NotAuthorized are terminal; Offline is transient (the
      // context can become authenticated when connectivity returns), and
      // NotAuthenticated is the pre-login state — both keep the latch.
      // Note: NotAuthorized users whose tree renders the guard screen
      // INSTEAD of app-stack never reach this discard — that's fine, the
      // app-container forcing is transient (never persisted), so the
      // forcing simply ends with the session and the saved preference is
      // untouched.
      const isTerminalNonAuthenticated =
        context !== null &&
        (context.isGuestMode ||
          context.authStatus === AuthStatus.NotAuthorized);
      if (isTerminalNonAuthenticated) {
        consumeBootStorefrontIntent();
      }
      return;
    }

    const intent = consumeBootStorefrontIntent();
    if (!intent) {
      return;
    }

    // Persisting the OPERATOR_PORTAL switch is the single place a /storefront
    // deep link is allowed to rewrite the saved experience preference — but
    // only once we've committed to applying the intent. Doing it before the
    // restaurant-access check below would force-persist the operator switch
    // even for an intent we then drop, stranding e.g. a sales-portal user
    // who deep-linked a restaurant they can't access. So it lives inside each
    // apply branch. The app-container forcing is transient and evaporates the
    // moment the intent is consumed, so a dropped intent correctly leaves the
    // saved preference untouched. skipUrlReset: the store's pushState('/')
    // would clobber the deep-link URL; getState() avoids subscribing this
    // effect to the store.
    const persistOperatorExperience = () =>
      useExperienceStore
        .getState()
        .switchToExperience(ExperienceMode.OPERATOR_PORTAL, {
          skipUrlReset: true,
        });

    if (
      intent.restaurantUUID === undefined ||
      intent.restaurantUUID === context.selectedRestaurantUUID
    ) {
      // No switch needed — navigate under the current restaurant.
      persistOperatorExperience();
      setPendingStorefrontNavigation({ productId: intent.productId });
      return;
    }

    if (!context.restaurantUUIDToRelationshipMap[intent.restaurantUUID]) {
      // The user has no verified access to this restaurant: drop the intent
      // silently — no error UI, no navigation, and (per above) no persisted
      // experience switch. Never pass an unverified UUID to selectRestaurant:
      // it persists unvalidated values, and garbage permanently overwrites
      // the user's saved selection before falling back to
      // restaurantRelationships[0].
      return;
    }

    // Persist the intent first, then switch via context.selectRestaurant —
    // the picker's exact code path, so timezone/Fennel/Intercom/setupApp/
    // remount behave identically. Deliberately NO location_picker
    // analytics event (this is a deep-link, not a manual location change).
    // The switch remounts the keyed
    // subtree; the remounted useStorefrontBootNavigation instance performs
    // the navigation once the new restaurant is selected.
    persistOperatorExperience();
    setPendingStorefrontNavigation(intent);
    context.selectRestaurant(intent.restaurantUUID);
  }, [context]);

  const clearPendingStorefrontNavigation = useCallback(() => {
    setPendingStorefrontNavigation(null);
  }, []);

  return { clearPendingStorefrontNavigation, pendingStorefrontNavigation };
};
